import React, { useState } from "react";
import { Typography, Button, Box, Grid, Autocomplete, TextField, Snackbar, Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Layout from "./UserLayout";
import { formFieldStyles, labelStyles } from "../../styles/formStyles";
import { signUp } from "../../services/User";
import { ISignUpDTO } from "../../interfaces/User";

interface Country {
  iso2: string;
  dialCode: string;
}

const countries: Country[] = [
  { iso2: "us", dialCode: "+1" },
  { iso2: "gb", dialCode: "+44" },
  { iso2: "in", dialCode: "+91" },
  { iso2: "ca", dialCode: "+1" },
  { iso2: "au", dialCode: "+61" },
  { iso2: "de", dialCode: "+49" },
  { iso2: "fr", dialCode: "+33" },
  { iso2: "jp", dialCode: "+81" },
  { iso2: "cn", dialCode: "+86" },
  { iso2: "br", dialCode: "+55" },
];

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    countryCode: "",
    phone: "",
    companyName: "",
  });
  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    country: "",
    phone: "",
    companyName: "",
  });
    const [loading,setLoading] = useState(false);
    const [errMsg, setErrMsg] = useState("");
    const [openError, setOpenError] = useState(false);

  const validate = (): boolean => {
    const newErrors = {
      firstName: "",
      lastName: "",
      email: "",
      country: "",
      phone: "",
      companyName: "",
    };
    let valid = true;

    if (!formData.firstName) {
      newErrors.firstName = "First name is required";
      valid = false;
    }

    if (!formData.lastName) {
      newErrors.lastName = "Last name is required";
      valid = false;
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Enter valid email";
      valid = false;
    }

    if (!formData.country) {
      newErrors.country = "Country is required";
      valid = false;
    }

    if (!formData.phone) {
      newErrors.phone = "Phone is required";
      valid = false;
    } else if (!/^[0-9]+$/.test(formData.phone)) {
      newErrors.phone = "Only numbers allowed";
      valid = false;
    } else if (formData.phone.length < 6) {
      newErrors.phone = "Too short";
      valid = false;
    }

    if (!formData.companyName) {
      newErrors.companyName = "Company is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const post = {} as ISignUpDTO;
      post.firstName = formData.firstName;
      post.lastName = formData.lastName;
      post.email = formData.email;
      post.country = formData.country;
      post.countryCode = formData.countryCode;
      post.phone = formData.phone;;
      post.companyName = formData.companyName;
      const response = await signUp(post);
      console.log("signup res:", response)
      if ( response && response.status === "success") {
      navigate("/otp-verification", {
        state: { email: formData.email },
      });
      } else if (response && response.status === "error"){
        const errMsg = response.errorResponse?.response?.data?.message || response.errorResponse?.message || "Signup Failed.";
        setErrMsg(errMsg);
        setOpenError(true);
      }
    } catch (error) {
      console.error(error);
      setErrMsg("Signup failed. Please try again.");
      setOpenError(true);
    } finally {
      setLoading(false);
    }
  };
  const handleSnackbarClose = () => {
    setOpenError(false);
  };
  return (
    <Layout>
      <Typography variant="h5" mb={1.5}>
        Sign up
      </Typography>

      <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <Typography component="label" sx={labelStyles}>
              First Name <span style={{ color: "#FE0909" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="First Name"
              value={formData.firstName}
              onChange={(e) =>
                setFormData({ ...formData, firstName: e.target.value })
              }
              error={!!errors.firstName}
              helperText={errors.firstName}
              sx={formFieldStyles}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography component="label" sx={labelStyles}>
              Last Name <span style={{ color: "#FE0909" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={(e) =>
                setFormData({ ...formData, lastName: e.target.value })
              }
              error={!!errors.lastName}
              helperText={errors.lastName}
              sx={formFieldStyles}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography component="label" sx={labelStyles}>
              Email Address <span style={{ color: "#FE0909" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              error={!!errors.email}
              helperText={errors.email}
              sx={formFieldStyles}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography component="label" sx={labelStyles}>
              Country <span style={{ color: "#FE0909" }}>*</span>
            </Typography>
            <Autocomplete
              size="small"
              options={countries}
              getOptionLabel={(option) =>
                `${option.iso2.toUpperCase()} (${option.dialCode})`
              }
              value={
                countries.find((c) => c.iso2 === formData.country) || null
              }
              onChange={(event, newValue) => {
                if (newValue) {
                  setFormData({
                    ...formData,
                    country: newValue.iso2,
                    countryCode: newValue.dialCode,
                  });
                } else {
                  setFormData({
                    ...formData,
                    country: "",
                    countryCode: "",
                  });
                }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.iso2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <img
                      src={`https://flagcdn.com/w20/${option.iso2}.png`}
                      width={18}
                      alt={option.iso2}
                    />
                    {option.iso2.toUpperCase()} ({option.dialCode})
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search country..."
                  error={!!errors.country}
                  helperText={errors.country}
                  sx={formFieldStyles}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography component="label" sx={labelStyles}>
              Phone Number <span style={{ color: "#FE0909" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Phone Number"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              error={!!errors.phone}
              helperText={errors.phone}
              sx={formFieldStyles}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography component="label" sx={labelStyles}>
              Company Name <span style={{ color: "#FE0909" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Company Name"
              value={formData.companyName}
              onChange={(e) =>
                setFormData({ ...formData, companyName: e.target.value })
              }
              error={!!errors.companyName}
              helperText={errors.companyName}
              sx={formFieldStyles}
            />
          </Grid>
        </Grid>

        {!loading && (
        <Button
          onClick={handleSubmit}
          fullWidth
          sx={{
            mt: 2.5,
            borderRadius: "6px",
            background: "#005EFF",
            color: "#fff",
            fontWeight: 600,
            py: 1.2,
            textTransform: "none",
            "&:hover": { background: "#0047cc" },
          }}
          endIcon={<ArrowForwardIcon />}
        >
          Sign Up
        </Button>
        )}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        )}

      <Box mt={2} textAlign="center">
        <Typography component="span">Already have an account? </Typography>
        <Typography
          component="span"
          onClick={() => navigate("/login")}
          sx={{
            color: "#005EFF",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          Sign In
        </Typography>
      </Box>

      <Snackbar anchorOrigin={{ vertical: "top", horizontal: "right" }} open={openError} autoHideDuration={5000} onClose={handleSnackbarClose}>
        <Alert severity="error" variant="filled" sx={{ width: "100%" }}>{errMsg}</Alert>
      </Snackbar>
    </Layout>
  );
};

export default Signup;
